import { INTAKE_QUESTIONS } from "./intake-copy";
import { getAnswer, validateStep, visibleSteps } from "./runtime";
import type {
  QuestionnaireDefinition,
  QuestionnaireField,
  QuestionnaireAnswers,
  QuestionnaireCurrency,
} from "./schema";

export const VALUATION_FLOW_VERSION = "2";
export const VALUATION_CURRENCIES = {
  us: "USD",
  ca: "CAD",
  nz: "NZD",
} as const;
export type ValuationCountry = keyof typeof VALUATION_CURRENCIES;
const base = (id: string, label: string) => ({
  id,
  binding: id,
  label,
  required: true,
  sensitivity: "standard" as const,
});

export function valuationDefinition(
  country: ValuationCountry,
  intake?: QuestionnaireDefinition,
  legacy = false,
): QuestionnaireDefinition {
  const currency: QuestionnaireCurrency = VALUATION_CURRENCIES[country];
  const fields: QuestionnaireField[] = [
    {
      ...base("industry", "What kind of business do you own?"),
      kind: "business_type",
      source: { system: "valuation", reference: "valuation.businessType" },
    },
    {
      ...base("country", "Which country is your business in?"),
      kind: "enum",
      options: [
        { id: "us", label: "United States" },
        { id: "ca", label: "Canada" },
        { id: "nz", label: "New Zealand" },
      ],
    },
    {
      ...base("region", "What state, province, or region is it in?"),
      kind: "text",
      minLength: 2,
      maxLength: 100,
    },
    {
      ...base("city", "And which city or town?"),
      kind: "text",
      minLength: 2,
      maxLength: 100,
    },
    {
      ...base("revenue", INTAKE_QUESTIONS.annual_revenue.label),
      description: INTAKE_QUESTIONS.annual_revenue.description,
      kind: "money",
      currencies: [currency],
      defaultCurrency: currency,
    },
    {
      ...base(
        "earnings",
        "How much money did the business make for you in the last 12 months?",
      ),
      description:
        "Include profit left after expenses, plus any salary you paid yourself. A rough number is fine.",
      kind: "text",
      source: { system: "valuation", reference: "valuation.earnings" },
      pattern: "^(not sure|[-+]?[0-9]+(?:\\.[0-9]{1,2})?)$",
      maxLength: 30,
    },
    {
      ...base("email", "What’s your email address?"),
      kind: "text",
      pattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
      maxLength: 254,
      source: { system: "valuation", reference: "contact.email" },
    },
  ];
  if (intake) {
    const countryField = fields.find((field) => field.id === "country");
    if (countryField?.kind === "enum")
      countryField.options = countryField.options.filter(
        (option) => option.id === country,
      );
  }
  const steps = fields.map((field) => ({
    id: field.id,
    title: field.label,
    fields: [field],
  }));
  const extra =
    intake?.steps.flatMap((step) => {
      const remaining = step.fields.filter(
        (field) =>
          field.kind !== "page" &&
          !["industry", "email", "revenue"].includes(coreAlias(field) ?? ""),
      );
      return remaining.length ? [{ ...step, fields: remaining }] : [];
    }) ?? [];
  return {
    id: "free-valuation",
    version: legacy ? "1" : VALUATION_FLOW_VERSION,
    title: "Find out what your business could be worth",
    initialStepId: "industry",
    steps: legacy
      ? [...steps, ...extra]
      : [
          ...steps.filter((step) => step.id === "industry"),
          {
            id: "location",
            title: "Where’s your business?",
            fields: fields
              .filter((field) =>
                ["country", "region", "city"].includes(field.id),
              )
              .map((field) => ({
                ...field,
                source: {
                  system: "valuation",
                  reference: "valuation.location",
                },
              })),
          },
          ...steps.filter((step) =>
            ["revenue", "earnings", "email"].includes(step.id),
          ),
          ...extra,
        ],
  };
}

/** Only aliases with a lossless, validated conversion may skip an intake question. */
export function coreAlias(
  field: QuestionnaireField,
): "industry" | "email" | "revenue" | "fullName" | "phone" | null {
  if (
    field.source?.reference === "business.businessTypeId" &&
    field.kind === "business_type"
  )
    return "industry";
  if (field.source?.reference === "contact.email") return "email";
  if (field.source?.reference === "contact.fullName" && field.kind === "text")
    return "fullName";
  if (field.source?.reference === "contact.phone" && field.kind === "phone")
    return "phone";
  if (
    field.analyticsKey === "intake.annual_revenue" &&
    field.kind === "enum" &&
    ["under_250k", "250k_1m", "1m_5m", "5m_10m", "over_10m"].every((id) =>
      field.options.some((o) => o.id === id),
    )
  )
    return "revenue";
  return null;
}
export function revenueBand(amount: number) {
  return amount < 250000
    ? "under_250k"
    : amount < 1000000
      ? "250k_1m"
      : amount < 5000000
        ? "1m_5m"
        : amount < 10000000
          ? "5m_10m"
          : "over_10m";
}
export function mapValuationIntakeAnswers(
  answers: QuestionnaireAnswers,
  intake: QuestionnaireDefinition,
): QuestionnaireAnswers {
  const result = { ...answers };
  for (const field of intake.steps.flatMap((step) => step.fields)) {
    const alias = coreAlias(field);
    if (!alias) continue;
    const value = getAnswer(answers, alias);
    if ((alias === "fullName" || alias === "phone") && value === undefined)
      continue;
    result[field.binding] =
      alias === "revenue"
        ? revenueBand(
            Number((value as { amountMinor?: number })?.amountMinor) / 100,
          )
        : value;
  }
  return result;
}
export function valuationIntakeErrors(
  answers: QuestionnaireAnswers,
  intake: QuestionnaireDefinition,
) {
  const mapped = mapValuationIntakeAnswers(answers, intake);
  return visibleSteps(intake, mapped).flatMap(
    (step) => validateStep({ step, answers: mapped }).errors,
  );
}
